import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Themes } from 'themes';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { ContactComponent } from 'app/shared/components/contact/contact.component';
import { BannerComponent } from 'app/shared/components/banner/banner.component';
import { IPayPalConfig, NgxPayPalModule } from 'ngx-paypal';
import { NGXLogger } from 'ngx-logger';
import { BackendService } from 'app/shared/services/backend.service';
import { BackendErrorType } from 'app/shared/services/errors/backend.errors';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { AuthenticationStateService } from 'app/shared/services/state/authentication-state.service';
import { Contract } from 'app/shared/models/contract.model';
import { FeeInfoBoxComponent } from './fee-info-box/fee-info-box.component';
import { isPrivateCustomer } from 'app/shared/models/customer.model';

const GENERIC_ERROR_MESSAGE = 'Ein Fehler ist aufgetreten, bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.';

@Component({
  selector: 'ez-paypal-detail',
  imports: [MainLayoutComponent, ContactComponent, BannerComponent, NgxPayPalModule, FeeInfoBoxComponent],
  templateUrl: './paypal-detail.component.html',
  styleUrl: './paypal-detail.component.scss',
})
export class PaypalDetailComponent {
  private readonly logger = inject(NGXLogger);
  private readonly router = inject(Router);
  private readonly authenticationStateService = inject(AuthenticationStateService);
  private readonly backendService = inject(BackendService);

  readonly landingpageConfig = inject(LandingpageConfigurationStateService).landingpageConfiguration;

  // ngx-paypal wraps every SDK callback in NgZone.run(), which is a pass-through under zoneless,
  // so this must be a signal for anything the PayPal SDK reports to reach the template.
  readonly errorMessage = signal<string | null>(null);

  readonly earliestDueDate = computed(() => {
    const landingpageConfig = this.landingpageConfig();
    return landingpageConfig ? this.getEarliestDueDate(landingpageConfig.contracts) : undefined;
  });

  readonly paypalConfig = computed(() => {
    const landingpageConfig = this.landingpageConfig();
    return landingpageConfig
      ? this.buildPaypalConfig(landingpageConfig.paymentConfiguration.paypalPaymentConfiguration!.clientId)
      : undefined;
  });

  private buildPaypalConfig(clientId: string): IPayPalConfig {
    return {
      fundingSource: 'PAYPAL',
      clientId: clientId,
      currency: 'EUR',
      createOrderOnServer: (data) => {
        this.logger.log('createOrderOnServer', data);
        this.errorMessage.set(null);
        const personalAccessToken = this.authenticationStateService.personalAccessToken()!;
        return new Promise((resolve, reject) => {
          return this.backendService.createPaypalOrder(personalAccessToken).subscribe({
            next: (response) => resolve(response.checkoutId),
            error: (error) => {
              this.handleBackendError(error);
              reject('there was an error while trying to create the order');
            },
          });
        });
      },
      onApprove: (data, actions) => {
        this.logger.log('onApprove', data, actions);
        const personalAccessToken = this.authenticationStateService.personalAccessToken()!;
        return this.backendService.capturePaypalOrder(personalAccessToken, data.orderID).subscribe({
          next: () => {
            this.router.navigate(['completed']);
          },
          error: (error) => this.handleBackendError(error),
        });
      },
      onError: (error) => {
        this.logger.log('onError', error);
        this.errorMessage.set(GENERIC_ERROR_MESSAGE);
      },
    };
  }

  onBackToOverview() {
    this.router.navigate(['case']);
  }

  private handleBackendError(error: Error) {
    if (error.message === BackendErrorType.ALREADY_SENT) {
      this.router.navigate(['already-sent']);
    } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
      this.router.navigate(['verification-error']);
    } else if (error.message === BackendErrorType.SERVER_ERROR) {
      this.router.navigate(['error']);
    } else {
      this.errorMessage.set(GENERIC_ERROR_MESSAGE);
    }
  }

  private getEarliestDueDate(contracts: Contract[]): Date {
    return contracts
      .flatMap((contract) => {
        return contract.items.map((item) => {
          return item.dueDate;
        });
      })
      .reduce((earlyDueDate, dueDate) => {
        return earlyDueDate < dueDate ? earlyDueDate : dueDate;
      });
  }

  protected readonly Themes = Themes;
  protected readonly isPrivateCustomer = isPrivateCustomer;
}
